import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  BlockHeader,
  FORM_PRIMARY_BUTTON_SX,
  FORM_SECONDARY_BUTTON_SX,
  PUBLICATION_BLOCK_SX,
} from '../../Map/components/DockPanel/Tabs/formBlockUi';
import {
  listSearchFieldSx,
  listSearchIconSx,
} from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { colors, typography } from '../../styles/theme';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { useScrollEdgeFade, getScrollEdgeFadeSx } from '../../hooks/useScrollEdgeFade';
import {
  CAD_PROJECT_PIN_KIND_CUSTOM,
  CAD_PROJECT_PIN_KINDS,
  cadProjectPinKindLabel,
  resolveCadProjectPinKind,
} from '../data/cadProjectPinKinds';
import {
  buildCadProjectPinDraft,
  filterCadProjectPinsByQuery,
  getCadProjectPinKindColor,
  normalizeCadProjectPin,
  readCadProjectPins,
} from '../utils/cadProjectPins';
import {
  createCadProjectPin,
  deleteCadProjectPin,
  updateCadProjectPin,
} from '../../services/cadService';
import {
  showroomCardSx,
  showroomFieldSx,
  showroomFormFooterSx,
  showroomScrollSx,
} from '../Showroom.styles';

const MAX_PINS = 50;
const MAX_NAME = 120;
const MAX_NOTES = 500;
const MAX_SEARCH = 80;

const HINT_SX = {
  color: colors.textSecondary,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: 1.45,
};

const PICK_BANNER_SX = {
  border: `1px solid ${colors.primary}`,
  bgcolor: 'rgba(72,146,145,0.12)',
  borderRadius: '8px',
  px: 1.5,
  py: 1.25,
  color: colors.textPanelTitle,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: 1.4,
};

const KIND_CHIP_SX = {
  height: 28,
  fontFamily: typography.fontFamily.primary,
  fontSize: '12px',
  fontWeight: 500,
  borderRadius: '16px',
  '& .MuiChip-label': { px: 1.25 },
};

/**
 * Manage de pines propios de la lista CAD. Owner / editor (`can_edit`).
 * Colocar = click en el mapa (MapPage / plan). No es Referenciar ni POI.
 * `canDelete=false` (invitado en espacio ajeno): crea y edita, no borra
 * (`/deletecadprojectpin` → 404 para el space editor).
 */
export default function CadProjectPinsPanel({
  project,
  paintMode = 'map',
  placing = false,
  onPlacingChange,
  placeCoords = null,
  onPlaceCoordsConsumed,
  onProjectPinsChange,
  onDraftChange = null,
  onHoverPinId = null,
  onCancel,
  selectedPinId = null,
  onSelectPinId,
  canDelete = true,
}) {
  const pins = useMemo(() => readCadProjectPins(project), [project]);
  const projectId = String(project?.id || project?.project_id || '').trim();
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(() => emptyDraft());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [pinSearch, setPinSearch] = useState('');
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();
  const listRef = useRef(null);
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  /** Ignora placeCoords anteriores a este instante (alta/edición). */
  const placeAcceptAfterRef = useRef(0);
  const setListNode = (node) => {
    listRef.current = node;
    scrollRef(node);
  };

  const visiblePins = useMemo(
    () => filterCadProjectPinsByQuery(pins, pinSearch),
    [pins, pinSearch],
  );
  const searchActive = Boolean(String(pinSearch || '').trim());

  useEffect(() => {
    refreshScrollEdges();
  }, [pins, visiblePins, editingId, placing, pinSearch, refreshScrollEdges]);

  useEffect(() => {
    if (!placeCoords || !placing) return;
    const ts = Number(placeCoords.ts) || 0;
    if (ts <= placeAcceptAfterRef.current) return;
    setDraft((prev) => ({
      ...prev,
      ...coordsFromPlace(placeCoords, paintMode),
    }));
    onPlaceCoordsConsumed?.();
  }, [placeCoords, placing, paintMode, onPlaceCoordsConsumed]);

  useEffect(() => {
    if (!selectedPinId) return;
    const pin = pins.find((row) => row.id === selectedPinId);
    if (!pin) return;
    setEditingId(pin.id);
    setDraft(draftFromPin(pin));
    placeAcceptAfterRef.current = Date.now();
    onHoverPinId?.(null);
    onPlacingChange?.(true);
    onSelectPinId?.(null);
  }, [selectedPinId, pins, onPlacingChange, onSelectPinId, onHoverPinId]);

  const atCap = pins.length >= MAX_PINS;
  const formOpen = placing || Boolean(editingId);
  const draftLocated = hasCoords(draft, paintMode);
  const canSave = Boolean(draft.name.trim()) && draftLocated && !saving;

  useEffect(() => {
    const report = onDraftChangeRef.current;
    if (!report) return undefined;
    if (!formOpen || !draftLocated) {
      report(null);
      return undefined;
    }
    report(buildCadProjectPinDraft(draft, { editingId }));
    return undefined;
  }, [draft, draftLocated, editingId, formOpen]);

  useEffect(() => () => {
    onDraftChangeRef.current?.(null);
  }, []);

  const startCreate = () => {
    if (atCap) {
      toast.error(`Podés agregar hasta ${MAX_PINS} pines.`);
      return;
    }
    setEditingId(null);
    setDraft(emptyDraft());
    placeAcceptAfterRef.current = Date.now();
    onHoverPinId?.(null);
    onPlaceCoordsConsumed?.();
    onPlacingChange?.(true);
  };

  const startEdit = (pin) => {
    setEditingId(pin.id);
    setDraft(draftFromPin(pin));
    placeAcceptAfterRef.current = Date.now();
    onHoverPinId?.(null);
    onPlaceCoordsConsumed?.();
    onPlacingChange?.(true);
  };

  const cancelForm = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    onPlacingChange?.(false);
    onDraftChangeRef.current?.(null);
  };

  const handleSave = async () => {
    const name = draft.name.trim();
    if (!name) {
      toast.error('Escribí un nombre para el pin.');
      return;
    }
    if (!hasCoords(draft, paintMode)) {
      toast.error(paintMode === 'plan'
        ? 'Hacé click en el plano para ubicar el pin.'
        : 'Hacé click en el mapa para ubicar el pin.');
      return;
    }
    setSaving(true);
    try {
      const kind = resolveCadProjectPinKind(draft.kind);
      const notes = draft.notes.trim() || null;
      let result;
      if (editingId) {
        result = await updateCadProjectPin({
          id: editingId,
          name,
          kind,
          notes,
          ...coordsPayload(draft, paintMode),
        });
      } else {
        result = await createCadProjectPin({
          project_id: projectId,
          name,
          kind,
          notes,
          ...coordsPayload(draft, paintMode),
        });
      }
      if (!result.ok || !result.pin) {
        toast.error(result.message || 'No se pudo guardar el pin.');
        return;
      }
      const pin = normalizeCadProjectPin(result.pin) || {
        ...normalizeCadProjectPin({
          id: editingId || result.pin.id,
          name,
          kind,
          notes,
          ...coordsPayload(draft, paintMode),
        }),
      };
      onProjectPinsChange?.(editingId ? 'update' : 'create', pin);
      toast.success(editingId ? 'Pin actualizado.' : 'Pin agregado.');
      cancelForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pin) => {
    if (!canDelete) return;
    const confirm = await confirmDialog({
      title: '¿Eliminar este pin?',
      description: `"${pin.name}" se saca de la lista. No se puede deshacer.`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm?.isConfirmed) return;
    setDeletingId(pin.id);
    try {
      const result = await deleteCadProjectPin({ id: pin.id });
      if (!result.ok) {
        toast.error(result.message || 'No se pudo eliminar el pin.');
        return;
      }
      onProjectPinsChange?.('delete', pin);
      if (editingId === pin.id) cancelForm();
      toast.success('Pin eliminado.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <BlockHeader title="Pines de la lista" />
        <Typography sx={{ ...HINT_SX, mt: 1 }}>
          Marcá en el mapa accesos, plazas, áreas verdes u otros puntos del desarrollo.
        </Typography>
      </Box>

      {placing ? (
        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Box sx={PICK_BANNER_SX} role="status">
            {editingId
              ? (paintMode === 'plan'
                ? 'Click en el plano para mover el pin; cuando esté listo, guardá.'
                : 'Click en el mapa para mover el pin; cuando esté listo, guardá.')
              : (draftLocated
                ? (paintMode === 'plan'
                  ? 'Pin marcado en el plano. Otro click lo mueve; cuando esté listo, guardá.'
                  : 'Pin marcado en el mapa. Otro click lo mueve; cuando esté listo, guardá.')
                : (paintMode === 'plan'
                  ? 'El cursor es una cruz: hacé click en el plano para ubicar el pin.'
                  : 'El cursor es una cruz: hacé click en el mapa para ubicar el pin.'))}
          </Box>
        </Box>
      ) : null}

      {formOpen ? (
        <Box sx={{
          ...PUBLICATION_BLOCK_SX,
          mx: 2.5,
          mb: 1.5,
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
        >
          <TextField
            label="Nombre"
            value={draft.name}
            onChange={(event) => setDraft((prev) => ({
              ...prev,
              name: event.target.value.slice(0, MAX_NAME),
            }))}
            size="small"
            fullWidth
            autoFocus
            sx={showroomFieldSx}
          />
          <Box>
            <Typography sx={{ ...HINT_SX, mb: 1, color: colors.textPanelTitle }}>
              Tipo
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {CAD_PROJECT_PIN_KINDS.map((row) => {
                const selected = resolveCadProjectPinKind(draft.kind) === row.id;
                const color = getCadProjectPinKindColor(row.id);
                return (
                  <Chip
                    key={row.id}
                    label={row.label}
                    onClick={() => setDraft((prev) => ({ ...prev, kind: row.id }))}
                    sx={{
                      ...KIND_CHIP_SX,
                      bgcolor: selected ? color : 'transparent',
                      color: selected ? colors.backgroundLight : colors.textPanelTitle,
                      border: `1px solid ${selected ? color : colors.border}`,
                      '&:hover': {
                        bgcolor: selected ? color : 'rgba(255,255,255,0.06)',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          <TextField
            label="Notas (opcional)"
            value={draft.notes}
            onChange={(event) => setDraft((prev) => ({
              ...prev,
              notes: event.target.value.slice(0, MAX_NOTES),
            }))}
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={showroomFieldSx}
          />
          <Typography sx={HINT_SX}>
            {draftLocated
              ? 'Ubicación marcada en el mapa. Podés guardar o hacer click de nuevo para moverlo.'
              : 'Todavía falta la ubicación: click en el mapa (cursor en cruz).'}
          </Typography>
        </Box>
      ) : null}

      {!formOpen && pins.length > 0 ? (
        <Box sx={{ px: 2.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar por nombre, tipo o notas"
            value={pinSearch}
            onChange={(event) => setPinSearch(event.target.value.slice(0, MAX_SEARCH))}
            sx={{ ...listSearchFieldSx, mt: 0 }}
            inputProps={{ maxLength: MAX_SEARCH, 'aria-label': 'Buscar pines' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={listSearchIconSx} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      ) : null}

      <Box
        ref={setListNode}
        sx={{
          ...showroomScrollSx,
          flex: 1,
          minHeight: 0,
          px: 2.5,
          pb: 1,
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        {!formOpen && pins.length === 0 ? (
          <Typography sx={{ ...HINT_SX, py: 2 }}>
            Todavía no hay pines en esta lista.
          </Typography>
        ) : null}
        {!formOpen && pins.length > 0 && visiblePins.length === 0 ? (
          <Typography sx={{ ...HINT_SX, py: 2 }}>
            {searchActive
              ? 'No hay resultados que coincidan.'
              : 'Todavía no hay pines en esta lista.'}
          </Typography>
        ) : null}
        {!formOpen ? visiblePins.map((pin) => (
          <Box
            key={pin.id}
            onMouseEnter={() => onHoverPinId?.(pin.id)}
            onMouseLeave={() => onHoverPinId?.(null)}
            sx={{
              ...showroomCardSx,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.25,
              mb: 1,
              cursor: 'default',
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: getCadProjectPinKindColor(pin.kind),
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: colors.textPanelTitle,
                lineHeight: 1.3,
              }}
              >
                {pin.name}
              </Typography>
              <Typography
                sx={{
                  ...HINT_SX,
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {cadProjectPinKindLabel(pin.kind)}
                {pin.notes ? ` · ${pin.notes}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25 }}>
              <IconButton
                size="small"
                aria-label={`Editar ${pin.name}`}
                onClick={() => startEdit(pin)}
                sx={{ color: colors.textSecondary }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              {canDelete ? (
                <IconButton
                  size="small"
                  aria-label={`Eliminar ${pin.name}`}
                  onClick={() => handleDelete(pin)}
                  disabled={deletingId === pin.id}
                  sx={{ color: colors.textSecondary }}
                >
                  {deletingId === pin.id
                    ? <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                    : <DeleteOutlineIcon fontSize="small" />}
                </IconButton>
              ) : null}
            </Box>
          </Box>
        )) : null}
      </Box>

      <Box sx={showroomFormFooterSx}>
        {formOpen ? (
          <>
            <Button
              variant="outlined"
              onClick={cancelForm}
              disabled={saving}
              sx={FORM_SECONDARY_BUTTON_SX}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canSave}
              startIcon={saving
                ? <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                : null}
              sx={FORM_PRIMARY_BUTTON_SX}
            >
              {editingId ? 'Guardar' : 'Agregar'}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={FORM_SECONDARY_BUTTON_SX}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={startCreate}
              disabled={atCap}
              startIcon={<AddLocationAltOutlinedIcon />}
              sx={FORM_PRIMARY_BUTTON_SX}
            >
              Agregar pin
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

function emptyDraft() {
  return {
    name: '',
    kind: CAD_PROJECT_PIN_KIND_CUSTOM,
    notes: '',
    lat: null,
    lng: null,
    local_x: null,
    local_y: null,
  };
}

function draftFromPin(pin) {
  return {
    name: pin.name || '',
    kind: resolveCadProjectPinKind(pin.kind),
    notes: pin.notes || '',
    lat: pin.lat,
    lng: pin.lng,
    local_x: pin.local_x,
    local_y: pin.local_y,
  };
}

function coordsFromPlace(placeCoords, paintMode) {
  if (paintMode === 'plan') {
    return {
      local_x: Number(placeCoords.local_x),
      local_y: Number(placeCoords.local_y),
      lat: null,
      lng: null,
    };
  }
  return {
    lat: Number(placeCoords.lat),
    lng: Number(placeCoords.lng),
    local_x: null,
    local_y: null,
  };
}

function hasCoords(draft, paintMode) {
  if (paintMode === 'plan') {
    if (draft.local_x == null || draft.local_y == null) return false;
    return Number.isFinite(Number(draft.local_x)) && Number.isFinite(Number(draft.local_y));
  }
  if (draft.lat == null || draft.lng == null) return false;
  return Number.isFinite(Number(draft.lat)) && Number.isFinite(Number(draft.lng));
}

function coordsPayload(draft, paintMode) {
  if (paintMode === 'plan') {
    return { local_x: Number(draft.local_x), local_y: Number(draft.local_y) };
  }
  return { lat: Number(draft.lat), lng: Number(draft.lng) };
}
